/**
 * Admin 공통 폼: Form.ts 활용 (async, await, try/catch, submitting, disabled, loader)
 * data-admin-ajax="1" 인 폼에 자동 바인딩. 제출 시 fetch + JSON 응답 처리, redirect 또는 메시지.
 * admin-auth__form 은 AdminAuthController에서 처리하므로 제외.
 */

import { bindForm as bindWrappedForm, bindForms as bindWrappedForms } from "../../core/FormWrapper";
import { NumberStepper } from "../../components/NumberStepper";

/**
 * Backward-compatible admin form binding API.
 * FormWrapper owns all admin mutating forms; this module keeps old imports stable.
 */
export function bindAdminForm(form: HTMLFormElement): void {
  bindWrappedForm(form);
}

/**
 * data-admin-ajax="1" 폼들을 scope 안에서 모두 바인딩. 이미 바인딩된 폼은 스킵.
 * 동적으로 새 폼이 DOM 에 추가됐을 때 호출.
 */
export function bindAdminForms(scope: ParentNode = document): void {
  bindWrappedForms(scope);
}

export function initAdminForms(): void {
  bindAdminForms(document);
  initTagPickers(document);
}

/**
 * 태그 피커 — [data-tag-picker] 컨테이너 내 체크박스 클릭 시 .is-selected 토글.
 * 체크 상태 자체는 브라우저가 유지하므로 JS는 시각 클래스 동기화만 담당.
 */
export function initTagPickers(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>("[data-tag-picker]:not([data-tag-picker-inited])").forEach(picker => {
    picker.dataset.tagPickerInited = "1";
    picker.addEventListener("change", (e) => {
      const input = e.target as HTMLInputElement;
      input.closest(".admin-tag-chip")?.classList.toggle("is-selected", input.checked);
    });
  });
}

/**
 * admin 영역의 date, datetime-local, time 입력: 인풋 클릭 시 선택기 열기 (showPicker).
 * 브라우저 기본은 우측 아이콘만 클릭 시 열리는 경우가 있어, 인풋 클릭 시 showPicker() 호출로 통일.
 */
export function initAdminDateTimeInputs(): void {
  const root = document.body.classList.contains("admin") ? document.body : document.querySelector(".admin");
  if (!root) return;

  const inputs = root.querySelectorAll<HTMLInputElement>(
    'input[type="date"], input[type="datetime-local"], input[type="time"]',
  );
  inputs.forEach((input) => {
    input.addEventListener("click", () => {
      if (typeof input.showPicker === "function") {
        try {
          input.showPicker();
        } catch {
          // 일부 브라우저/상황에서는 showPicker 미지원
        }
      }
    });
  });
}

import { DatePicker } from "../../components/DatePicker";
import { SelectWrapper } from "../../components/SelectWrapper";

/**
 * admin 영역의 [data-datetime-wrap] / [data-time-wrap] 요소에 Flatpickr 적용.
 * native showPicker() 와 별개로, 커스텀 래퍼 스타일의 날짜/시간 선택기를 초기화.
 */
export function initAdminFlatpickrPickers(): void {
  if (!document.body.classList.contains("admin")) return;

  document.querySelectorAll<HTMLElement>("[data-datetime-wrap]").forEach((wrap) => {
    const input = wrap.querySelector<HTMLElement>("input");
    if (!input) return;
    new DatePicker(input, {
      enableTime: true,
      dateFormat: "Y-m-d H:i",
      time24hr: true,
      allowInput: false,
    }).init();
    wrap.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).tagName !== "INPUT") input.dispatchEvent(new MouseEvent("click", { bubbles: false }));
    });
  });

  document.querySelectorAll<HTMLElement>("[data-time-wrap]").forEach((wrap) => {
    const input = wrap.querySelector<HTMLElement>("input");
    if (!input) return;
    new DatePicker(input, {
      enableTime: true,
      noCalendar: true,
      dateFormat: "H:i",
      time24hr: true,
      allowInput: false,
    }).init();
    wrap.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).tagName !== "INPUT") input.dispatchEvent(new MouseEvent("click", { bubbles: false }));
    });
  });

  document.querySelectorAll<HTMLElement>("[data-daterange-wrap]").forEach((wrap) => {
    const display = wrap.querySelector<HTMLInputElement>("input.admin-daterange-input");
    const startSel = wrap.getAttribute("data-daterange-start") ?? "";
    const endSel = wrap.getAttribute("data-daterange-end") ?? "";
    const startEl = startSel ? wrap.querySelector<HTMLInputElement>(startSel) : null;
    const endEl = endSel ? wrap.querySelector<HTMLInputElement>(endSel) : null;
    if (!display || !startEl || !endEl) return;

    const toYmd = (d: Date): string => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    // 편집 복원: hidden 값(Y-m-d 또는 Y-m-d H:i)의 앞 10자만 초기 선택값으로.
    const initStart = startEl.value.slice(0, 10);
    const initEnd = endEl.value.slice(0, 10);
    const defaultDate: string[] = [];
    if (initStart) defaultDate.push(initStart);
    if (initEnd && initEnd !== initStart) defaultDate.push(initEnd);

    new DatePicker(display, {
      mode: "range",
      dateFormat: "Y-m-d",
      allowInput: false,
      defaultDate: defaultDate.length > 0 ? defaultDate : undefined,
      onChange: (selectedDates) => {
        const start = selectedDates[0] ? toYmd(selectedDates[0]) : "";
        // range 에서 종료 미선택(시작만 클릭) 시 단일 일정 → start=end.
        const end = selectedDates[1] ? toYmd(selectedDates[1]) : start;
        startEl.value = start;
        endEl.value = end;
      },
    }).init();

    wrap.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).tagName !== "INPUT") display.dispatchEvent(new MouseEvent("click", { bubbles: false }));
    });
  });
}

/**
 * admin 영역의 모든 select에 SlimSelect 적용 (SelectWrapper 사용).
 * body.admin 내 select.admin-select, select.admin-form__input, select.admin-auth__input 대상.
 *
 * root 파라미터로 동적 추가된 폼 일부도 init 가능. 이중 init 방지를 위해
 * data-admin-select-inited 마크 사용.
 */
export function initAdminSelects(root: ParentNode = document): void {
  if (!document.body.classList.contains("admin")) return;

  const selects = (root as Element | Document).querySelectorAll<HTMLSelectElement>(
    "select.admin-select:not([data-admin-select-inited]):not([data-locale-add-select]):not([data-locale-select]):not([data-card-select]), select.admin-form__input:not([data-admin-select-inited]):not([data-card-select]), select.admin-auth__input:not([data-admin-select-inited]):not([data-card-select])",
  );
  selects.forEach((el) => {
    el.dataset.adminSelectInited = "1";
    const addable = el.hasAttribute("data-addable");
    const searchPosition = el.dataset.searchPosition === "top" ? "top" as const : undefined;
    const rich = el.dataset.rich === "1";
    new SelectWrapper(el, { searchPlaceholder: "검색...", addable, searchPosition, rich }).init();

    if (el.dataset.perPageNav) {
      el.addEventListener("change", () => {
        const u = new URL(location.href);
        u.searchParams.set("per_page", el.value);
        u.searchParams.delete("page");
        location.href = u.toString();
      });
    }
  });
}

/**
 * TagInput — [data-tag-input] 요소에 바인딩.
 * Enter / 쉼표로 태그 추가, × 버튼으로 제거. hidden input 배열로 값 유지.
 */
export function initTagInputs(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-tag-input]:not([data-tag-inited])").forEach(el => {
    el.dataset.tagInited = "1";
    const maxTags   = el.dataset.maxTags  ? parseInt(el.dataset.maxTags,  10) : Infinity;
    const maxLength = el.dataset.maxLength ? parseInt(el.dataset.maxLength, 10) : Infinity;
    const validate  = el.dataset.validate || "";
    const field     = el.querySelector<HTMLInputElement>(".admin-tag-input__field");
    const tagsWrap  = el.querySelector<HTMLElement>(".admin-tag-input__tags");
    if (!field || !tagsWrap) return;

    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    function isValid(val: string): boolean {
      if (validate === "email") return EMAIL_RE.test(val);
      return true;
    }

    const hiddenName = (() => {
      // 컴포넌트가 항상 내보내는 정확한 폼 필드명(예: reply_to_email[])을 최우선 사용.
      if (el.dataset.name) return el.dataset.name;
      const first = tagsWrap.querySelector<HTMLInputElement>("input[type=hidden]");
      if (first) return first.name;
      const id = el.id || "";
      return id ? `${id}[]` : "tags[]";
    })();

    function countTags(): number {
      return tagsWrap!.querySelectorAll(".admin-tag-input__chip").length;
    }

    function reject(): void {
      // 형식 위반 등으로 칩 추가가 거부됐을 때 짧은 시각 피드백.
      el.classList.add("admin-tag-input--error");
      window.setTimeout(() => el.classList.remove("admin-tag-input--error"), 800);
    }

    function addTag(raw: string): boolean {
      const val = raw.trim();
      if (!val || val.length > maxLength) return false;
      if (countTags() >= maxTags) return false;
      if (!isValid(val)) { reject(); return false; }
      // 중복 방지
      const existing = [...tagsWrap!.querySelectorAll<HTMLInputElement>("input[type=hidden]")].map(i => i.value);
      if (existing.includes(val)) return false;

      const chip = document.createElement("span");
      chip.className = "admin-tag-input__chip";

      const lbl = document.createElement("span");
      lbl.className = "admin-tag-input__chip-label";
      lbl.textContent = val;

      const rmBtn = document.createElement("button");
      rmBtn.type = "button";
      rmBtn.className = "admin-tag-input__chip-remove";
      rmBtn.setAttribute("aria-label", `${val} 제거`);
      rmBtn.innerHTML = `<svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;
      // 제거는 tagsWrap 위임 핸들러가 처리(서버 렌더 칩·동적 칩 일원화).

      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = hiddenName;
      hidden.value = val;

      chip.append(lbl, rmBtn, hidden);
      tagsWrap!.appendChild(chip);
      return true;
    }

    // Enter/쉼표 없이 저장 버튼을 누르는 경우에도 마지막 입력값을 유실하지 않는다.
    // FormWrapper의 submit 핸들러가 FormData를 만드는 시점보다 먼저 칩으로 확정한다.
    // capture 단계로 등록해야 FormWrapper의 bubble 단계보다 먼저 실행된다.
    const form = el.closest("form");
    form?.addEventListener("submit", () => {
      if (!field.value.trim()) return;
      if (addTag(field.value)) field.value = "";
    }, { capture: true });

    field.addEventListener("keydown", e => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        if (addTag(field.value)) field.value = "";
      } else if (e.key === "Backspace" && !field.value) {
        tagsWrap!.querySelector(".admin-tag-input__chip:last-child")?.remove();
      }
    });

    // × 제거 — 이벤트 위임: 서버 렌더 칩과 동적 추가 칩 모두 처리.
    tagsWrap.addEventListener("click", e => {
      const btn = (e.target as HTMLElement).closest(".admin-tag-input__chip-remove");
      if (!btn) return;
      e.stopPropagation();
      btn.closest(".admin-tag-input__chip")?.remove();
    });

    el.addEventListener("click", () => field.focus());
  });
}

export function initNumberSteppers(root: ParentNode = document): void {
  NumberStepper.initAll(root);
}

/**
 * AdminSearchBars — [data-admin-search-bar] 요소에 바인딩.
 * clear 버튼(data-admin-search-clear)을 JS로 제어.
 * - 초기값 있으면 hidden 제거, 없으면 hidden 유지
 * - input 이벤트마다 value 유무로 hidden 토글
 * - clear 클릭 시 value 비우고 input focus 복원
 */
export function initAdminSearchBars(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-admin-search-bar]:not([data-search-bar-inited])").forEach(wrap => {
    wrap.dataset.searchBarInited = "1";
    const input = wrap.querySelector<HTMLInputElement>(".admin-search-bar__input");
    const clearBtn = wrap.querySelector<HTMLButtonElement>("[data-admin-search-clear]");
    if (!input || !clearBtn) return;

    function syncClear(): void {
      if (input!.value !== "") {
        clearBtn!.removeAttribute("hidden");
      } else {
        clearBtn!.setAttribute("hidden", "");
      }
    }

    input.addEventListener("input", syncClear);
    clearBtn.addEventListener("click", () => {
      input.value = "";
      syncClear();
      input.focus();
    });

    syncClear();
  });
}

/**
 * CharCounter — [data-char-counter] 요소에 바인딩.
 * textarea 입력마다 글자 수 갱신. 80% 이상 warn, 100% limit 클래스 추가.
 */
export function initCharCounters(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-char-counter]:not([data-counter-inited])").forEach(wrap => {
    wrap.dataset.counterInited = "1";
    const textarea = wrap.querySelector<HTMLTextAreaElement>("textarea");
    if (!textarea) return;
    const maxlength = parseInt(wrap.dataset.maxlength ?? textarea.maxLength?.toString() ?? "0", 10) || 0;
    if (!maxlength) return;

    // label 영역의 카운터 span — data-char-counter wrap의 형제 .admin-char-counter__header 를 탐색
    const header  = wrap.closest(".admin-form__group, .admin-form__field, .admin-form__block")
                      ?.querySelector<HTMLElement>("[data-char-count]")
                  ?? wrap.previousElementSibling?.querySelector<HTMLElement>("[data-char-count]")
                  ?? null;
    const current = header?.querySelector<HTMLElement>("[data-char-current]") ?? null;

    function refresh(): void {
      const len = textarea!.value.length;
      if (current) current.textContent = String(len);
      const ratio = maxlength > 0 ? len / maxlength : 0;
      current?.classList.toggle("admin-char-counter__current--warn",  ratio >= 0.8 && ratio < 1);
      current?.classList.toggle("admin-char-counter__current--limit", ratio >= 1);
      wrap.classList.toggle("admin-char-counter__wrap--warn",  ratio >= 0.8 && ratio < 1);
      wrap.classList.toggle("admin-char-counter__wrap--limit", ratio >= 1);
    }

    textarea.addEventListener("input", refresh);
    refresh();
  });
}
